import type { CampaignMetadata, TelemetryEventInput, TerminalResult } from '@upl/probe-telemetry';

const ENDPOINT = '/v1/probe-telemetry';
let measurementEnabled = false;
let pendingGap: { gap_token: string; gap_started_at: string; gap_ended_at: string } | null = readStoredGap();

export type ConsentState = 'granted' | 'denied' | 'unset' | 'unavailable';

export async function getMeasurementConsent(): Promise<ConsentState> {
  try {
    const response = await fetch(`${ENDPOINT}/consent`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) return 'unavailable';
    const body = await response.json() as { status?: string };
    measurementEnabled = body.status === 'granted';
    if (measurementEnabled) await ensureMeasurementSession();
    return body.status === 'granted' || body.status === 'denied' ? body.status : 'unset';
  } catch {
    return 'unavailable';
  }
}

async function ensureMeasurementSession(): Promise<void> {
  await fetch(`${ENDPOINT}/session`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(readAcquisition()),
  });
}

export async function setMeasurementConsent(decision: 'granted' | 'denied'): Promise<boolean> {
  const acquisition = readAcquisition();
  try {
    const response = await fetch(`${ENDPOINT}/consent`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ decision, ...acquisition }),
    });
    measurementEnabled = response.ok && decision === 'granted';
    return response.ok;
  } catch {
    measurementEnabled = false;
    return false;
  }
}

export async function emitProbeEvent(input: Omit<TelemetryEventInput, 'event_id' | 'occurred_at'>): Promise<boolean> {
  if (!measurementEnabled) return false;
  if (pendingGap) await flushGap();
  const event = { ...input, event_id: crypto.randomUUID(), occurred_at: new Date().toISOString() };
  try {
    const response = await fetch(`${ENDPOINT}/events`, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(event),
    });
    if (response.ok) return true;
    await captureGap(response);
    return false;
  } catch {
    return false;
  }
}

export async function beginMeasuredVerification(): Promise<{ interactionId: string; verificationContext: string } | null> {
  if (!measurementEnabled) return null;
  try {
    const response = await fetch(`${ENDPOINT}/interactions/start`, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: '{}',
    });
    if (!response.ok) return null;
    const body = await response.json() as { interaction_id?: string; verification_context?: string };
    return body.interaction_id && body.verification_context
      ? { interactionId: body.interaction_id, verificationContext: body.verification_context }
      : null;
  } catch { return null; }
}

export function terminalResultForStatus(status: string): TerminalResult {
  if (status === 'VERIFIED_PROVENANCE' || status === 'PROVENANCE_PRESENT') return 'PROVENANCE_EVIDENCE_DETECTED';
  if (status === 'NO_SUPPORTED_PROVENANCE') return 'NO_SUPPORTED_PROVENANCE_EVIDENCE';
  return 'BOUNDED_SUPPORTED_RESULT';
}

export function currentCampaign(): CampaignMetadata | undefined {
  return readAcquisition().campaign;
}

async function captureGap(response: Response): Promise<void> {
  if (response.status !== 503) return;
  try {
    const value = await response.json() as typeof pendingGap;
    if (value?.gap_token && value.gap_started_at && value.gap_ended_at) {
      pendingGap = { gap_token: value.gap_token, gap_started_at: value.gap_started_at, gap_ended_at: value.gap_ended_at };
      try { sessionStorage.setItem('prvbl_probe_pending_gap', JSON.stringify(pendingGap)); } catch { /* unavailable storage */ }
    }
  } catch {
    // A completely unavailable endpoint cannot issue a signed gap token. Hosting logs remain the fallback.
  }
}

async function flushGap(): Promise<void> {
  const gap = pendingGap;
  if (!gap) return;
  pendingGap = null;
  const response = await fetch(`${ENDPOINT}/events`, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ event_id: crypto.randomUUID(), event_type: 'TELEMETRY_GAP', occurred_at: new Date().toISOString(), ...gap }),
  });
  if (!response.ok) pendingGap = gap;
  else try { sessionStorage.removeItem('prvbl_probe_pending_gap'); } catch { /* unavailable storage */ }
}

function readStoredGap(): typeof pendingGap {
  try {
    const value = JSON.parse(sessionStorage.getItem('prvbl_probe_pending_gap') ?? 'null') as typeof pendingGap;
    return value?.gap_token && value.gap_started_at && value.gap_ended_at ? value : null;
  } catch {
    return null;
  }
}

function readAcquisition(): { referrer_host?: string; campaign?: CampaignMetadata } {
  const params = new URLSearchParams(globalThis.location.search);
  const campaign: CampaignMetadata = {};
  for (const [query, key] of [['utm_source', 'source'], ['utm_medium', 'medium'], ['utm_campaign', 'campaign']] as const) {
    const value = params.get(query)?.toLowerCase();
    if (value && /^[a-z0-9._~-]{1,100}$/.test(value)) campaign[key] = value;
  }
  let referrerHost: string | undefined;
  try { referrerHost = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : undefined; } catch { referrerHost = undefined; }
  return {
    ...(referrerHost ? { referrer_host: referrerHost } : {}),
    ...(Object.keys(campaign).length ? { campaign } : {}),
  };
}
